import { LotacaoEditableTableModel } from 'components/form/editable-table/LotacaoEditableTable'
import {
  AtividadeColetivaQueryInput,
  AtividadeColetivaTableQuery,
  FatoAtividadeColetivaTableQuery,
} from 'graphql/types.generated'
import { dimensaoSexoByIdentificadorRecord, turnoByStringRecord } from 'types/enums'
import { v4 as uuidv4 } from 'uuid'
import {
  AtividadeColetivaFilterFormModel,
  AtividadeColetivaReuniaoModel,
  AtividadeColetivaSaudeModel,
  FatoAtividadeColetivaReuniaoModel,
  FatoAtividadeColetivaSaudeModel,
  tipoAtividadeByIdRecord,
} from 'view/atividade-coletiva/model-atividadeColetiva'

import { CidadaoGrupoItemModel, LotacaoGrupoItemModel } from './componentes/AtividadeColetivaGrupoSelectField'
import { ParticipanteEditableTableModel } from './componentes/participantes/ParticipanteEditableTable'

const convertNomeCnsCpf = (nomeCnsCpf: string | undefined) => {
  const trimmed = nomeCnsCpf?.trim()

  return trimmed?.length < 3 ? undefined : trimmed
}

export const convertAtividadeColetivaFilterFormModelToInput = (
  model: AtividadeColetivaFilterFormModel,
  profissionalCpfCns: string
): AtividadeColetivaQueryInput => ({
  isReuniao: !!model.isReuniao,
  equipesIne: model.equipes?.map((equipe) => equipe.ine) ?? [],
  isProcessado: model.isProcessado,
  nomeCnsCpfProfissionalResponsavel: model.isSomenteRegistrosProprios
    ? profissionalCpfCns
    : convertNomeCnsCpf(model.nomeCnsCpfProfissionalResponsavel),
  periodo: model.periodo,
  tiposAtividade: model.tiposAtividade ?? [],
  origens: model.origens ?? [],
  pageParams: model.pageParams,
})

export const convertFatoAtividadeColetivaTableQueryToAtividadeColetivaTableQuery = (
  fatoAtividadesColetivas?: FatoAtividadeColetivaTableQuery['fatoAtividadesColetivas']
): AtividadeColetivaTableQuery['atividadesColetivas'] => {
  return (
    fatoAtividadesColetivas && {
      content: fatoAtividadesColetivas.content.map((fatoAtividadeColetiva) => ({
        id: fatoAtividadeColetiva.id,
        dataAtividade: fatoAtividadeColetiva.dataRegistro,
        tipoAtividade:
          fatoAtividadeColetiva.dimensaoTipoAtividade?.nuIdentificador &&
          tipoAtividadeByIdRecord[fatoAtividadeColetiva.dimensaoTipoAtividade.nuIdentificador],
        lotacaoResponsavel: fatoAtividadeColetiva.dimensaoProfissional && {
          id: fatoAtividadeColetiva.dimensaoProfissional.id,
          profissional: {
            id: fatoAtividadeColetiva.dimensaoProfissional.id,
            nome: fatoAtividadeColetiva.dimensaoProfissional.nome,
          },
          cbo: fatoAtividadeColetiva.dimensaoCbo && {
            id: fatoAtividadeColetiva.dimensaoCbo.id,
            nome: fatoAtividadeColetiva.dimensaoCbo.nome,
          },
        },
        numeroParticipantes: fatoAtividadeColetiva.nuParticipantes,
        registroEnviado: true,
        origem: fatoAtividadeColetiva.origem,
      })),
      pageInfo: fatoAtividadesColetivas.pageInfo,
    }
  )
}

export const convertFatoAtividadeColetivaReuniaoToAtividadeColetiva = (
  fatoAtividadeColetiva: FatoAtividadeColetivaReuniaoModel
): AtividadeColetivaReuniaoModel => {
  return {
    ...convertFatoAtividadeColetivaToAtividadeColetiva(fatoAtividadeColetiva),
    numeroAvaliacoesAlteradas: fatoAtividadeColetiva.nuAvaliacoesAlteradas,
    temasReuniao: fatoAtividadeColetiva.temasReuniao,
    participantes: fatoAtividadeColetiva.fatoAtividadeColetivaParticipantes?.map((participante) => ({
      ...convertParticipante(participante),
    })),
  }
}

export const convertFatoAtividadeColetivaSaudeToAtividadeColetiva = (
  fatoAtividadeColetiva: FatoAtividadeColetivaSaudeModel
): AtividadeColetivaSaudeModel => {
  return {
    ...convertFatoAtividadeColetivaToAtividadeColetiva(fatoAtividadeColetiva),
    numeroAvaliacoesAlteradas: fatoAtividadeColetiva.nuAvaliacoesAlteradas,
    publicoAlvo: fatoAtividadeColetiva.publicoAlvo,
    praticasSaude: fatoAtividadeColetiva.praticasSaude,
    procedimento: fatoAtividadeColetiva.dimensaoProcedimento && {
      id: fatoAtividadeColetiva.dimensaoProcedimento.id,
      descricao: fatoAtividadeColetiva.dimensaoProcedimento.descricaoProcedimento,
      codigo: fatoAtividadeColetiva.dimensaoProcedimento.codigoProcedimento,
    },
    participantes: fatoAtividadeColetiva.fatoAtividadeColetivaParticipantes?.map((participante) => ({
      peso: participante.nuPesoParticipante,
      altura: participante.nuAlturaParticipante,
      avaliacaoAlterada: participante.stAvaliacaoAlterada,
      pnctAbandonouGrupo: participante.stPnctAbandonouGrupo,
      pnctCessouHabitoFumar: participante.stPnctCessouHabitoFumar,
      ...convertParticipante(participante),
    })),
  }
}

const convertFatoAtividadeColetivaToAtividadeColetiva = (
  fatoAtividadeColetiva: FatoAtividadeColetivaReuniaoModel | FatoAtividadeColetivaSaudeModel
) => {
  return {
    id: fatoAtividadeColetiva.id,
    atividadeSaudePse: fatoAtividadeColetiva.stPseSaude,
    atividadeEducacaoPse: fatoAtividadeColetiva.stPseEducacao,
    origem: fatoAtividadeColetiva.origem,
    temasSaude: fatoAtividadeColetiva.temasSaude,
    inepLocalAtividade: fatoAtividadeColetiva.dimensaoInep && {
      id: fatoAtividadeColetiva.dimensaoInep.id,
      nomeEstabelecimento: fatoAtividadeColetiva.dimensaoInep.noEstabelecimento,
      dsInep: fatoAtividadeColetiva.dimensaoInep.nuIdentificador,
    },
    tipoAtividade:
      fatoAtividadeColetiva.dimensaoTipoAtividade?.nuIdentificador &&
      tipoAtividadeByIdRecord[fatoAtividadeColetiva.dimensaoTipoAtividade.nuIdentificador],
    turno:
      fatoAtividadeColetiva.dimensaoTurno &&
      turnoByStringRecord[fatoAtividadeColetiva.dimensaoTurno?.descricao.toLowerCase()],
    dataAtividade: fatoAtividadeColetiva.dataRegistro,
    numeroParticipantes: fatoAtividadeColetiva.nuParticipantes,
    outraLocalidade: fatoAtividadeColetiva.descricaoOutraLocalidade,
    unidadeSaudeLocalAtividade: fatoAtividadeColetiva.dimensaoUnddSadeAcdmSade && {
      id: fatoAtividadeColetiva.dimensaoUnddSadeAcdmSade.id,
      nome: fatoAtividadeColetiva.dimensaoUnddSadeAcdmSade.nome,
      cnes: fatoAtividadeColetiva.dimensaoUnddSadeAcdmSade.cnes,
    },

    lotacaoResponsavel: fatoAtividadeColetiva.dimensaoProfissional && {
      id: fatoAtividadeColetiva.dimensaoProfissional.id,
      profissional: {
        id: fatoAtividadeColetiva.dimensaoProfissional.id,
        nome: fatoAtividadeColetiva.dimensaoProfissional.nome,
        cpf: undefined, // TODO: ver necessidade de dados faltantes
      },
      cbo: fatoAtividadeColetiva.dimensaoCbo && {
        id: fatoAtividadeColetiva.dimensaoCbo.id,
        nome: fatoAtividadeColetiva.dimensaoCbo.nome,
        cbo2002: fatoAtividadeColetiva.dimensaoCbo.cbo,
      },
      unidadeSaude: fatoAtividadeColetiva.dimensaoUnidadeSaude && {
        id: fatoAtividadeColetiva.dimensaoUnidadeSaude.id,
        nome: fatoAtividadeColetiva.dimensaoUnidadeSaude.nome,
        cnes: fatoAtividadeColetiva.dimensaoUnidadeSaude.cnes,
      },
      equipe: fatoAtividadeColetiva.dimensaoEquipe && {
        id: fatoAtividadeColetiva.dimensaoEquipe.id,
        nome: fatoAtividadeColetiva.dimensaoEquipe.nome,
        ine: fatoAtividadeColetiva.dimensaoEquipe.ine,
      },
    },
    lotacoesEnvolvidas: fatoAtividadeColetiva.fatoAtividadeColetivaProfissionalParticipante?.map((lotacao) => ({
      id: lotacao.id,
      profissional: {
        id: lotacao.dimensaoProfissionalParticipante.id,
        nome: lotacao.dimensaoProfissionalParticipante.nome,
      },
      cbo: lotacao.dimensaoCboParticipante && {
        id: lotacao.dimensaoCboParticipante.id,
        nome: lotacao.dimensaoCboParticipante.nome,
      },
    })),
  }
}

const convertParticipante = (participante) => {
  return {
    id: participante.id,
    atividadeColetivaId: participante.fatoAtividadeColetiva.id,
    cidadaoParticipante: {
      id: participante.id, // TODO: confimar se é isso mesmo
      nome: undefined, // TODO ver necessidade de retornar nome
      cpf: participante.nuCpfParticipante,
      cns: participante.nuCnsParticipante,
      dataNascimento: participante?.dtNascimentoParticipante,
      sexo: participante.dimensaoSexo && dimensaoSexoByIdentificadorRecord[participante.dimensaoSexo.identificador],
    },
    cidadao: participante.fatoCidadaoPec && {
      id: participante.fatoCidadaoPec.id, // TODO: confimar se é isso mesmo
      nome: participante.fatoCidadaoPec.nome,
      nomeSocial: participante.fatoCidadaoPec.nomeSocial,
      cpf: participante.fatoCidadaoPec.cpf,
      cns: participante.fatoCidadaoPec.cns,
      dataNascimento: participante.fatoCidadaoPec.dataNascimento,
      sexo:
        participante.fatoCidadaoPec.dimensaoSexo &&
        dimensaoSexoByIdentificadorRecord[participante.fatoCidadaoPec.dimensaoSexo.identificador],
    },
  }
}

export const convertCidadaoGrupoItemModelToParticipanteEditableTableModel = (
  cidadao: CidadaoGrupoItemModel
): ParticipanteEditableTableModel => {
  return {
    _id: uuidv4(),
    isCidadaoParticipanteFormOpen: false,
    cidadao: {
      id: cidadao.id,
      cpf: cidadao.cpf,
      cns: cidadao.cns,
      dataNascimento: cidadao.dataNascimento,
      nome: cidadao.nome,
      nomeSocial: cidadao.nomeSocial,
      sexo: cidadao.sexo,
    },
    cidadaoParticipante: null,
    peso: null,
    altura: null,
    avaliacaoAlterada: false,
    pnctAbandonouGrupo: null,
    pnctCessouHabitoFumar: null,
  }
}

export const convertLotacaoGrupoItemModelToLotacaoEditableTableModel = (
  lotacao: LotacaoGrupoItemModel
): LotacaoEditableTableModel => ({
  _id: uuidv4(),
  lotacao: {
    id: lotacao.id,
    profissional: lotacao.profissional,
    unidadeSaude: lotacao.unidadeSaude,
    cbo: lotacao.cbo,
    equipe: lotacao.equipe
      ? {
          ...lotacao.equipe,
          isAD: false,
        }
      : null,
  },
})
